import { pipe } from 'fp-ts/lib/function';
import { failure, parse, string, success } from 'io-ts/lib/Decoder';

import { isUUID } from '@bedc/core';

/**
 * The prefixes comment ids carried before P22 (#2106): `comment:<prefix>|<uuid>`, with the
 * truss-HITL literal or an FPC variation id. Those rows are still in the table under that
 * exact, case-sensitive sort key, so the decoder must hand them back unchanged: `withPrefix`
 * lower-cases and its legacy character set has no `:`, which is why P22 broke updates and
 * deletes of every pre-existing comment.
 */
const reLegacyPrefix = /^(hitlRoof|fpc-job-variation:[0-9a-fA-F-]{36})$/;

/**
 * `comment:<uuid>` (lower-cased), or one of the legacy shapes above with the uuid lower-cased
 * and the prefix kept as stored. Anything else fails: the id is trusted as a DynamoDB key.
 */
export const Id = pipe(
	string,
	parse((raw) => {
		const expectedPrefix = 'comment:';

		if (!raw.startsWith(expectedPrefix)) {
			return failure(raw, 'a valid comment id');
		}

		const suffix = raw.slice(expectedPrefix.length);

		if (isUUID(suffix)) {
			return success(`${expectedPrefix}${suffix.toLowerCase()}`);
		}

		const separator = suffix.lastIndexOf('|');
		const legacyPrefix = suffix.slice(0, separator);
		const uuid = suffix.slice(separator + 1);

		if (separator > 0 && reLegacyPrefix.test(legacyPrefix) && isUUID(uuid)) {
			return success(`${expectedPrefix}${legacyPrefix}|${uuid.toLowerCase()}`);
		}

		return failure(raw, 'a valid comment id');
	})
);
